// Kid-selectable color themes (dark moods + accent). Kept in sync with the mobile
// registry in /app/mobile/src/theme.ts. Each theme is an all-dark background mood plus
// one accent that overrides the primary AND the four mode tints.
export const THEMES = {
  ocean:  { label: "Ocean",  bg: "#06172B", s2: "#10243A", s3: "#142B45", border: "#294663", deep: "#041222", accent: "#1684FF" },
  forest: { label: "Forest", bg: "#0B1D15", s2: "#122A1E", s3: "#173527", border: "#244A38", deep: "#07140E", accent: "#35D7A0" },
  onyx:   { label: "Onyx",   bg: "#0B0C0F", s2: "#15171C", s3: "#1D2027", border: "#2C313B", deep: "#060709", accent: "#22D3EE" },
  ember:  { label: "Ember",  bg: "#1C1510", s2: "#291E15", s3: "#33261A", border: "#46331F", deep: "#120D09", accent: "#FF8A3D" },
  rose:   { label: "Rose",   bg: "#1E0E1A", s2: "#2C1526", s3: "#371A2F", border: "#4A2740", deep: "#150A12", accent: "#FF5FA2" },
  grape:  { label: "Grape",  bg: "#150C28", s2: "#221539", s3: "#2A1B47", border: "#3B2A5C", deep: "#0E0720", accent: "#9D7BFF" },
  berry:  { label: "Berry",  bg: "#1C0F22", s2: "#2A1633", s3: "#341C40", border: "#482753", deep: "#14091A", accent: "#E96BE0" },
  bloom:  { label: "Bloom",  bg: "#241318", s2: "#331B22", s3: "#3E212A", border: "#52303A", deep: "#1A0D11", accent: "#FF7E6B" },
};

export const THEME_LIST = Object.entries(THEMES).map(([id, t]) => ({ id, ...t }));
export const DEFAULT_THEME = "ocean";

function hexToHslTriple(hex) {
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let hue = 0, s = 0;
  const l = (max + min) / 2;
  const d = max - min;
  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    if (max === r) hue = ((g - b) / d) % 6;
    else if (max === g) hue = (b - r) / d + 2;
    else hue = (r - g) / d + 4;
    hue *= 60;
    if (hue < 0) hue += 360;
  }
  return `${Math.round(hue)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export function applyTheme(id) {
  const t = THEMES[id] || THEMES[DEFAULT_THEME];
  const root = document.documentElement.style;
  const set = (k, hex) => root.setProperty(k, hexToHslTriple(hex));
  set("--background", t.bg);
  set("--card", t.s2);
  set("--popover", t.s2);
  set("--secondary", t.s2);
  set("--muted", t.s2);
  set("--input-bg", t.s3);
  set("--border", t.border);
  set("--input", t.border);
  set("--primary", t.accent);
  set("--accent", t.accent);
  set("--ring", t.accent);
  set("--deep", t.deep);
  ["ask", "learn", "create", "research"].forEach((m) => set(`--accent-${m}`, t.accent));
  // Expose the accent hue so chat backgrounds can derive complementary colors in CSS.
  root.setProperty("--accent-h", hexToHslTriple(t.accent).split(" ")[0]);
}
